import { useState } from 'react';
import { api } from '../lib/api';
import type { SettingsResponse } from '../dashboard/types';

/**
 * Shared sync-settings card set — the ONE config surface for tenant-wide sync
 * behavior (M14 QA #18). Rendered by two entry points:
 *
 *   - Dashboard → Settings view (wrapped in a ViewHeader).
 *   - Onboarding wizard → "Sync settings" step (between Connect and Products).
 *
 * Four independent toggles (each writes directly to D1 via POST admin/settings):
 *
 *   1. Create Deals for subscriptions (`tenants.sync_create_deals`).
 *   2. Create Deals for subscription changes / deltas (`tenants.sync_create_delta_deals`).
 *   3. Auto-link products by name (`tenants.auto_link_products_by_name`).
 *   4. Auto-create products (`tenants.auto_create_products`).
 *
 * Pipeline provisioning (creating the HubSpot pipelines) is NOT done here — it
 * is a bootstrap-time concern handled by POST onboarding/provision at "Start
 * Import" (see onboarding/StepImport). This component only persists the D1
 * toggles so the gate is in effect when the import / webhooks run.
 *
 * `onChanged` is called after a successful write so the parent can refetch the
 * full settings row (keeps every card in sync).
 *
 * Reference: docs/PHASE2-PLAN.md M14 (QA #18)
 *            src/handlers/dashboard.ts (handleSettingsGet/Update)
 *            pages/src/dashboard/Settings.tsx (dashboard entry point)
 */
export function SettingsCards({
  settings,
  onChanged,
  context = 'dashboard',
}: {
  settings: SettingsResponse;
  onChanged: () => void;
  /** Where the cards are rendered. In 'onboarding' the pipeline-provisioning
   *  note is suppressed (provisioning happens automatically at "Start Import",
   *  so the note would be a confusing Settings-page artifact leaked into the
   *  wizard — QA-F #2). */
  context?: 'dashboard' | 'onboarding';
}) {
  return (
    <div className="max-w-2xl space-y-6">
      <DealMirroringCard settings={settings} onChanged={onChanged} context={context} />
      <DeltaDealsCard settings={settings} onChanged={onChanged} />
      <ProductAutoLinkCard settings={settings} onChanged={onChanged} />
      <ProductAutoCreateCard settings={settings} onChanged={onChanged} />
    </div>
  );
}

/** Loading + error states for the settings read. */
export function SettingsStates({
  loading,
  error,
  onRetry,
}: {
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
}) {
  if (error) {
    return (
      <div className="max-w-2xl rounded-lg border border-danger-500/30 bg-danger-500/5 p-4 text-sm text-ink-600">
        <p className="font-medium text-danger-600">Couldn't load settings.</p>
        <p className="mt-1">{error.message}</p>
        <button className="btn-secondary mt-2" onClick={onRetry}>
          Try again
        </button>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="max-w-2xl space-y-4">
        <div className="card h-24 animate-pulse bg-ink-100/60" />
      </div>
    );
  }
  return null;
}

/** POST a partial settings update, returning the refreshed settings. */
export function postSetting(
  patch: Partial<SettingsResponse> & Pick<SettingsResponse, 'createDeals'>
): Promise<SettingsResponse> {
  return api.post<SettingsResponse>('admin/settings', patch);
}

/** The createDeals toggle card. */
function DealMirroringCard({
  settings,
  onChanged,
  context,
}: {
  settings: SettingsResponse;
  onChanged: () => void;
  context: 'dashboard' | 'onboarding';
}) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function toggle(next: boolean) {
    // Optimistic: nothing — the toggle button reflects the request while
    // saving; we refresh from the server response on success.
    setSaving(true);
    setSaveError(null);
    try {
      await postSetting({ createDeals: next });
      onChanged();
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to update setting.'
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-ink-900">
            Create Deals for subscriptions
          </h2>
          <p className="mt-1 text-sm text-ink-500">
            Mirror each Stripe Subscription as a Closed-Won HubSpot Deal in the
            Subscriptions pipeline. Turn off to stop creating Deals (mapping
            rows + the sidebar card keep working either way).
          </p>
        </div>
        <ToggleSwitch
          checked={settings.createDeals}
          disabled={saving}
          onChange={toggle}
          ariaLabel="Create Deals for subscriptions"
        />
      </div>

      {/* Pipeline-provisioning note. Suppressed during onboarding (QA-F #2):
          the wizard provisions the HubSpot pipelines automatically at "Start
          Import" regardless of where the toggle is set, so showing "pipelines
          aren't set up yet" mid-wizard is a confusing artifact. Only the
          post-onboarding dashboard context surfaces it — where it means "re-run
          provisioning" is genuinely needed. */}
      {context === 'dashboard' && !settings.pipelinesProvisioned && (
        <div className="mt-4 rounded-lg border border-warn-500/30 bg-warn-500/5 p-3 text-xs text-warn-700">
          <strong>Note:</strong> the deal pipelines aren't set up for this
          tenant. Deal mirroring is on, but no Deals will be created until the
          pipelines are provisioned. Re-run provisioning from the dashboard or
          contact support.
        </div>
      )}

      {saveError && (
        <p className="mt-3 text-xs text-danger-600">{saveError}</p>
      )}
    </div>
  );
}

/** Shared toggle-card factory for the two product auto-settings. */
function ProductToggleCard({
  title,
  description,
  checked,
  settingKey,
  settings,
  onChanged,
}: {
  title: string;
  description: string;
  checked: boolean;
  settingKey: 'autoLinkProductsByName' | 'autoCreateProducts';
  settings: SettingsResponse;
  onChanged: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function toggle(next: boolean) {
    setSaving(true);
    setSaveError(null);
    try {
      // createDeals is required by the endpoint — pass the current value
      // unchanged so only the product setting actually changes.
      await postSetting({ createDeals: settings.createDeals, [settingKey]: next });
      onChanged();
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to update setting.'
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-ink-900">{title}</h2>
          <p className="mt-1 text-sm text-ink-500">{description}</p>
        </div>
        <ToggleSwitch
          checked={checked}
          disabled={saving}
          onChange={toggle}
          ariaLabel={title}
        />
      </div>
      {saveError && (
        <p className="mt-3 text-xs text-danger-600">{saveError}</p>
      )}
    </div>
  );
}

/** Auto-link products by name. */
function ProductAutoLinkCard({
  settings,
  onChanged,
}: {
  settings: SettingsResponse;
  onChanged: () => void;
}) {
  return (
    <ProductToggleCard
      title="Auto-link products by name"
      description={'When a new Stripe price\u2019s derived name (product name + billing interval, e.g. \u201cPro Plan (Monthly)\u201d) exactly matches an existing HubSpot Product, link them automatically instead of queuing for review. No match or no suffix in your catalog? Enable auto-create instead.'}
      checked={settings.autoLinkProductsByName}
      settingKey="autoLinkProductsByName"
      settings={settings}
      onChanged={onChanged}
    />
  );
}

/** Auto-create products. */
function ProductAutoCreateCard({
  settings,
  onChanged,
}: {
  settings: SettingsResponse;
  onChanged: () => void;
}) {
  return (
    <ProductToggleCard
      title="Auto-create products"
      description="When a new Stripe Product has no name match (or auto-link is off), create a matching HubSpot Product automatically. Use with care on a messy Stripe catalog — every product becomes a HubSpot Product."
      checked={settings.autoCreateProducts}
      settingKey="autoCreateProducts"
      settings={settings}
      onChanged={onChanged}
    />
  );
}

/** Create deals for subscription deltas (upgrades/downgrades). */
function DeltaDealsCard({
  settings,
  onChanged,
}: {
  settings: SettingsResponse;
  onChanged: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function toggle(next: boolean) {
    setSaving(true);
    setSaveError(null);
    try {
      // createDeals is required by the endpoint — pass current values unchanged.
      await postSetting({
        createDeals: settings.createDeals,
        createDeltaDeals: next,
      });
      onChanged();
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to update setting.'
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-ink-900">
            Create deals for subscription changes
          </h2>
          <p className="mt-1 text-sm text-ink-500">
            Mirror upgrades and downgrades as delta Deals in the Subscription
            Changes pipeline. Independent of baseline deal mirroring — turn off
            to keep subscription Deals but declutter the change history. Only
            has an effect when deal mirroring is on.
          </p>
        </div>
        <ToggleSwitch
          checked={settings.createDeltaDeals}
          disabled={saving}
          onChange={toggle}
          ariaLabel="Create deals for subscription changes"
        />
      </div>
      {saveError && (
        <p className="mt-3 text-xs text-danger-600">{saveError}</p>
      )}
    </div>
  );
}

/** Accessible on/off toggle switch. */
function ToggleSwitch({
  checked,
  disabled,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={[
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-brand-600' : 'bg-ink-200',
      ].join(' ')}
    >
      <span
        className={[
          'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0.5',
        ].join(' ')}
      />
    </button>
  );
}
